import {
  ColumnDef,
  RowSelectionState,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { DataGridWithFooter } from '@/components/DataGrid';
import {
  emulateNativeClick,
  emulateNativeMiddleClick,
  getTableSelectColumnDef,
  useDataGridSizingLocalStorage,
} from '@/components/DataGrid.utils';
import { DataGridColumnVisibilityPopover } from '@/components/DataGrid/DataGridColumnVisibilityPopover';
import { DateTimeRangePicker } from '@/components/DateTimeRangePicker/DateTimeRangePicker';
import EmptyState from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { ConversationFilterBar } from '@/components/FilterBar/ConversationFilterBar';
import { FilterViewsSelector } from '@/components/FilterBar/FilterViewsSelect';
import { LatencyChip } from '@/components/RunLatencyChip';
import { LoadMoreButton } from '@/components/RunsTable/LoadMoreButton';
import { BulkActionResult } from '@/components/RunsTable/types';
import { filterInputs } from '@/components/RunsTable/utils/filterInputs';
import useToast from '@/components/Toast/useToast';
import { useEppoFeatureFlag } from '@/context/hooks/useInitializeEppo';
import { Button, ButtonGroup, Spinner } from '@/design-system';
import { GridTimeModel, useDataGridState } from '@/hooks/useDataGridState';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import MessageChatIcon from '@/icons/MessageChatIcon.svg?react';
import TokenIcon from '@/icons/TokenIcon.svg?react';
import TraceExpandIcon from '@/icons/TraceExpandIcon.svg?react';
import { FilterViewType, RunGroupSchema, RunStatsSchema } from '@/types/schema';

import { FeedbackChips } from '../../components/FeedbackChips';
import { useListFilterViews, useOrganizationId } from '../../hooks/useSwr';
import { ConversationPane } from './ConversationPane';
import SelectedConversationsActions from './SelectedConversationsActions';
import { getThreadUrl } from './utils/getThreadUrl';

const formatter = new Intl.NumberFormat('en-US', {});
const pluralRules = new Intl.PluralRules('en-US');
const columnHelper = createColumnHelper<RunGroupSchema>();

const EMPTY_TABLE = [];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const COLUMNS_DEF: ColumnDef<RunGroupSchema, any>[] = [
  columnHelper.accessor('group_key', {
    header: 'Thread',
    minSize: 200,
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-tertiary text-brand-green-400">
            <MessageChatIcon className="h-5 w-5" />
          </div>

          <div className="flex flex-col gap-0.5">
            <div>{row.original.group_key}</div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <TraceExpandIcon className="text-tertiary" />
                <span>
                  {formatter.format(row.original.count ?? 0)}{' '}
                  {
                    {
                      one: `trace`,
                      two: `traces`,
                      few: `traces`,
                      other: `traces`,
                    }[pluralRules.select(row.original.count ?? 0)]
                  }
                </span>
              </div>
              <div className="flex items-center gap-1">
                <TokenIcon className="text-tertiary" />
                <span>
                  {formatter.format(row.original.total_tokens ?? 0)}{' '}
                  {
                    {
                      one: `token`,
                      two: `tokens`,
                      few: `tokens`,
                      other: `tokens`,
                    }[pluralRules.select(row.original.total_tokens ?? 0)]
                  }
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    },
    enableSorting: false,
  }),

  columnHelper.accessor('first_inputs', {
    header: 'First Input',
    cell: ({ row }) => {
      if (row.original.first_inputs == null) return '';
      return filterInputs(row.original.first_inputs);
    },
    enableSorting: false,
  }),

  columnHelper.accessor('last_outputs', {
    header: 'Last Output',
    cell: ({ row }) => {
      if (row.original.last_error) {
        return <span className="text-error">{row.original.last_error}</span>;
      }
      if (row.original.last_outputs == null) return '';
      return (
        filterInputs(row.original.last_outputs) ??
        String(row.original.last_error)
      );
    },
    enableSorting: false,
  }),

  columnHelper.accessor('min_start_time', {
    header: 'First Start Time',
    cell: ({ row }) => {
      return new Date(row.original.min_start_time + 'Z').toLocaleString();
    },
    enableSorting: false,
  }),

  columnHelper.accessor('max_start_time', {
    header: 'Last Start Time',
    cell: ({ row }) => {
      return new Date(row.original.max_start_time + 'Z').toLocaleString();
    },
    enableSorting: false,
  }),

  columnHelper.accessor('latency_p50', {
    header: 'P50 Latency',
    cell: ({ row }) => {
      return <LatencyChip latency={row.original.latency_p50} />;
    },
    enableSorting: false,
  }),

  columnHelper.accessor('latency_p99', {
    header: 'P99 Latency',
    cell: ({ row }) => {
      return <LatencyChip latency={row.original.latency_p99} />;
    },
    enableSorting: false,
  }),

  columnHelper.accessor('feedback_stats', {
    header: 'Feedback',
    cell: ({ row }) => (
      <div className="absolute inset-0 flex items-center gap-2 overflow-x-auto px-2.5 py-4 no-scrollbar">
        <FeedbackChips feedbackStats={row.original.feedback_stats} />
      </div>
    ),
    enableSorting: false,
    minSize: 300,
  }),
];

const COLUMN_VISIBILITY_STATE_KEY = 'ls:ui:conversations-table-columns';

export function ConversationList(props: {
  sessionId: string;
  groups: RunGroupSchema[];
  loadNextPage: () => void;
  hasMore: boolean;
  isLoading: boolean;

  timeModel: GridTimeModel;
  onTimeModelChange: (value: GridTimeModel) => void;

  filter: string | undefined;
  onFilterChange: (
    value: string | undefined,
    setter?: ReturnType<typeof useSearchParams>[1]
  ) => void;

  stats: RunStatsSchema | undefined;
  error?: Error | null;
}) {
  'use no memo';
  const navigate = useNavigate();

  const { value: showThreadImprovements } = useEppoFeatureFlag({
    featureFlag: 'thread_improvements',
    flagType: 'boolean',
    defaultValue: false,
  });

  const organizationId = useOrganizationId();
  const { sessionId } = useParams();

  const [columnSizing, setColumnSizing] =
    useDataGridSizingLocalStorage('conversations');

  const { columnVisibilityModel, setColumnVisibilityModel } = useDataGridState({
    columnVisibilityStateKey: COLUMN_VISIBILITY_STATE_KEY,
  });

  const data = props.groups ?? EMPTY_TABLE;

  const [rowSelection, _setRowSelection] = useState<RowSelectionState>({});

  const { value: enableThreadViewPlayground } = useOrgConfig(
    OrgConfigs.enable_thread_view_playground
  );

  const setRowSelection: Dispatch<SetStateAction<RowSelectionState>> =
    useCallback(
      (action) => {
        _setRowSelection(action);
      },
      [rowSelection]
    );

  const hasSelectedRuns = Object.keys(rowSelection).length > 0;
  const selectedConversations = useMemo(
    () =>
      data?.filter(
        (row) =>
          Object.keys(rowSelection).includes(row.group_key) &&
          rowSelection[row.group_key]
      ) ?? [],
    [rowSelection, data]
  );

  const { createToast } = useToast();

  const setBulkActionResult = (result: BulkActionResult) => {
    createToast({
      description: result.message,
      error: !result.success,
      title: result.title,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const experimentColumns: ColumnDef<RunGroupSchema, any>[] =
    enableThreadViewPlayground
      ? [getTableSelectColumnDef({ variant: 'virtual' })]
      : [];

  const table = useReactTable<RunGroupSchema>({
    data,
    columns: experimentColumns.concat(COLUMNS_DEF),
    state: {
      columnSizing,
      rowSelection,
      columnVisibility: columnVisibilityModel,
    },

    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibilityModel,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.group_key,

    columnResizeMode: 'onChange',

    manualSorting: true,
    manualPagination: true,
  });

  const { data: threadFilterViews, isLoading: isLoadingThreadFilterViews } =
    useListFilterViews(sessionId, FilterViewType.THREADS);

  const containerRef = useRef<HTMLDivElement>(null);

  const [filterBarOpen, setFilterBarOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('custom_filter_view_id') ?? undefined;
  const peekedConversationId =
    searchParams.get('peekedConversationId') ?? undefined;

  const onViewChange = (newView: string, filter?: string) => {
    const filterViewId = newView;
    const isTogglingOff = filterViewId === view || !filterViewId;
    props.onFilterChange(isTogglingOff ? '' : filter, setSearchParams);
    setSearchParams((params) => {
      if (isTogglingOff) {
        params.delete('custom_filter_view_id');
      } else {
        params.set('custom_filter_view_id', filterViewId);
      }
      return params;
    });
  };
  const emptyState = props.filter ? (
    <EmptyState
      title="No matching threads"
      description="Try adjusting your search criteria."
      action={
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://docs.langchain.com/langsmith/filter-traces-in-application#filter-traces"
        >
          <Button color="primary" variant="plain" size="md">
            Learn more about filters
          </Button>
        </a>
      }
    />
  ) : (
    <EmptyState
      title="No threads found"
      description="Threads are used to group Traces that form a single conversation or user interaction together."
      action={
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://docs.langchain.com/langsmith/threads"
        >
          <Button color="primary" size="md">
            Configure threads
          </Button>
        </a>
      }
    />
  );

  const handlePeekThread = (threadId: string) => {
    setSearchParams((params) => {
      params.set('peekedConversationId', threadId);
      return params;
    });
  };

  const handleClosePeek = () => {
    setSearchParams((params) => {
      params.delete('peekedConversationId');
      return params;
    });
  };

  const renderContent = () => {
    return (
      <>
        <DataGridWithFooter
          virtual={true}
          table={table}
          cellSpacingVariant="wide"
          cellHeight={64}
          isLoading={props.isLoading}
          highlightedId={
            showThreadImprovements ? peekedConversationId : undefined
          }
          onClick={(row, event) => {
            if (showThreadImprovements) {
              // Use peek functionality for thread improvements
              handlePeekThread(row.group_key);
            } else {
              // Original behavior
              const path = getThreadUrl(
                organizationId,
                sessionId,
                row.group_key
              );
              if (!emulateNativeClick(path, event.nativeEvent)) {
                navigate(path);
              }
            }
          }}
          onMiddleClick={(row) => {
            // Middle-click always opens in new tab, regardless of thread improvements
            const path = getThreadUrl(organizationId, sessionId, row.group_key);
            if (!emulateNativeMiddleClick(path)) {
              navigate(path);
            }
          }}
          emptyState={
            <div className="flex flex-col items-center py-10">{emptyState}</div>
          }
          footer={
            props.hasMore &&
            data.length > 0 && (
              <div className="flex flex-col items-center border-t border-secondary">
                <LoadMoreButton
                  isInitialLoad
                  isLoading={props.isLoading}
                  isVirtual={true}
                  onClick={() => {
                    props.loadNextPage();
                  }}
                />
              </div>
            )
          }
        />

        {hasSelectedRuns && sessionId && (
          <SelectedConversationsActions
            sessionId={sessionId}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            selectedConversations={selectedConversations}
            setBulkActionResult={setBulkActionResult}
            limit={20}
          />
        )}
      </>
    );
  };

  const peekedConversationIndex = data.findIndex(
    (row) => row.group_key === peekedConversationId
  );

  return (
    <>
      <div className="flex items-stretch justify-between gap-2 border-b border-secondary p-4">
        <div className="flex flex-1 items-center gap-2" ref={containerRef}>
          <ConversationFilterBar
            open={filterBarOpen}
            setOpen={setFilterBarOpen}
            stats={props.stats}
            value={props.filter}
            onChange={props.onFilterChange}
            onViewChange={onViewChange}
          />

          <DateTimeRangePicker
            value={props.timeModel}
            onChange={(value) => props.onTimeModelChange(value ?? {})}
            context="conversationsList"
          />

          <ButtonGroup variant="outlined" color="secondary">
            {threadFilterViews && threadFilterViews.length > 0 && (
              <FilterViewsSelector
                containerRef={containerRef}
                filterViews={threadFilterViews}
                view={view}
                onViewChange={onViewChange}
                setFilterBarOpen={setFilterBarOpen}
                defaultFilters={[]}
              />
            )}
            {isLoadingThreadFilterViews && (
              <div className="flex items-center justify-center px-1">
                <Spinner />
              </div>
            )}
          </ButtonGroup>
        </div>

        <DataGridColumnVisibilityPopover
          table={table}
          localStorageKey={COLUMN_VISIBILITY_STATE_KEY}
        />
      </div>
      {props.error ? (
        <div className="p-4">
          <ErrorBanner>{props.error.message}</ErrorBanner>
        </div>
      ) : (
        renderContent()
      )}
      {showThreadImprovements && (
        <ConversationPane
          open={!!peekedConversationId}
          onClose={handleClosePeek}
          onNext={
            peekedConversationIndex < data.length - 1
              ? () => {
                  handlePeekThread(data[peekedConversationIndex + 1].group_key);
                }
              : undefined
          }
          onPrevious={
            peekedConversationIndex > 0
              ? () => {
                  handlePeekThread(data[peekedConversationIndex - 1].group_key);
                }
              : undefined
          }
        />
      )}
    </>
  );
}
